import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import { FiX, FiCheck, FiPlus, FiFileText, FiClock, FiAlertCircle, FiInfo } from 'react-icons/fi';
import AddAdditionalDocumentsModal from './AddAdditionalDocumentsModal';
import CompliancePrompt from './compliance-ui/CompliancePrompt.jsx';
import '../styles/codiii-kit.css';
import { phScanModalOpened } from '../utils/posthogEvents';

const PATH_TYPE_LABELS = {
  conflict: 'Conflict Detection',
  // gap: 'Gap Detection',
  compliance_check: 'Compliance Check',
};

const PATH_TYPE_DESCRIPTIONS = {
  conflict: 'Finds contradictions between documents or requirements.',
  // gap: 'Finds topics present in one document but missing from another.',
  compliance_check: 'Verifies your documents against the Requirements Register.',
};

const COMPLIANCE_TYPES = [
  { value: 'BUILDING_CODE',       label: 'Building Code' },
  { value: 'CLIENT_REQUIREMENTS', label: 'Client Requirements' },
  // { value: 'ZONING',              label: 'Zoning' },
  { value: 'STANDARDS',           label: 'Standards' },
];

const BUILDING_CODE_SUBCATEGORIES = [
  { value: 'LIFE_SAFETY',              label: 'Life Safety' },
  { value: 'STRUCTURAL',               label: 'Structural' },
  { value: 'MECHANICAL',               label: 'Mechanical' },
  { value: 'ELECTRICAL',               label: 'Electrical' },
  { value: 'PLUMBING',                 label: 'Plumbing' },
  { value: 'ACCESSIBILITY',            label: 'Accessibility' },
  { value: 'ENERGY_AND_SUSTAINABILITY',label: 'Energy & Sustainability' },
];

const ComplianceScanModal = ({
  isOpen,
  onClose,
  onCancelScan,
  onStartScan,
  isScanActive = false,
  promptValue = '',
  onPromptChange,
  onVoiceToggle,
  isRecording = false,
  isRunning = false,
  showPromptLibrary = false,
  onTogglePromptLibrary,
  savedPrompts = [],
  onSavePrompt,
  onUsePrompt,
  onDeletePrompt,
  promptLibraryEmptyText,
  candidates = [],
  onContinue,
  projectDocs = [],
  pathType: detectedPathType,
  pathReasoning,
  pathConfidence,
  awaitingDocuments = false,
  projectId,
}) => {
  const { t } = useFlexibleTranslation();

  useEffect(() => {
    if (isOpen) phScanModalOpened({ projectId });
  }, [isOpen, projectId]);

  // Scanning progress state
  const [progress, setProgress] = useState(0);
  const [isScanning, setIsScanning] = useState(true);
  const [scanStatus, setScanStatus] = useState('scanning');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Docs state
  const [docs, setDocs] = useState([]);
  const [showAddDocsModal, setShowAddDocsModal] = useState(false);

  // Path state — user can override detected path
  const [confirmedPathType, setConfirmedPathType] = useState(null);
  const [conflictMode, setConflictMode] = useState(null); // dvd or rvr

  // Selection state
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [selectedComplianceTypes, setSelectedComplianceTypes] = useState([]);
  const [selectedComplianceType, setSelectedComplianceType] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [revealStage, setRevealStage] = useState(0); // 0:none, 1:path, 2:options, 3:docs
  const revealTimersRef = useRef([]);
  const candidatesFingerprintRef = useRef('');
  const autoSelectedCandidatesRef = useRef('');

  const getId = (doc) =>
    doc.docId ?? doc.id ?? doc.s3Key ?? doc.key ?? `${doc.bucket || ''}/${doc.title || doc.name || ''}`;

  const mergeCandidateDocs = (prev, incoming) => {
    const byId = new Map();
    prev.forEach((d) => byId.set(getId(d), d));
    incoming.forEach((d) => {
      const id = getId(d);
      const existing = byId.get(id);
      byId.set(id, existing ? { ...existing, ...d } : d);
    });
    return Array.from(byId.values()).sort((a, b) => {
      const scoreA = typeof a.score === 'number' ? a.score : -1;
      const scoreB = typeof b.score === 'number' ? b.score : -1;
      return scoreB - scoreA;
    });
  };

  // Merge ranked candidates from polling; never wipe user-added docs or clear on empty polls.
  useEffect(() => {
    if (!isOpen) return;
    const incoming = candidates || [];
    if (incoming.length === 0) return;

    const fingerprint = incoming.map(getId).sort().join('|');
    if (fingerprint !== candidatesFingerprintRef.current) {
      candidatesFingerprintRef.current = fingerprint;
      autoSelectedCandidatesRef.current = '';
    }

    setDocs((prev) => mergeCandidateDocs(prev, incoming));
  }, [candidates, isOpen]);

  // Close add docs modal when main modal closes
  useEffect(() => {
    if (!isOpen) setShowAddDocsModal(false);
  }, [isOpen]);

  // Reset everything when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setIsScanning(true);
    setIsSubmitting(false);
    setProgress(0);
    setScanStatus('scanning');
    setSelectedDocIds(new Set());
    setSelectedComplianceTypes([]);
    setSelectedComplianceType('');
    setSelectedSubcategories([]);
    setRevealStage(0);
    setConflictMode(null);
    setConfirmedPathType(null);
    setDocs([]);
    candidatesFingerprintRef.current = '';
    autoSelectedCandidatesRef.current = '';

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return Math.min(90, prev + Math.random() * 15);
      });
    }, 200);

    return () => clearInterval(interval);
  }, [isOpen]);

  useEffect(() => {
    revealTimersRef.current.forEach(clearTimeout);
    revealTimersRef.current = [];

    if (isScanning || !confirmedPathType) {
      setRevealStage(0);
      return;
    }

    // Progressive disclosure to reduce cognitive load after scan completion.
    setRevealStage(1);
    revealTimersRef.current.push(
      setTimeout(() => setRevealStage(2), 180),
      setTimeout(() => setRevealStage(3), 360)
    );

    return () => {
      revealTimersRef.current.forEach(clearTimeout);
      revealTimersRef.current = [];
    };
  }, [isScanning, confirmedPathType]);

  // Complete scan UI when backend is ready for user configuration (not only when docs exist).
  useEffect(() => {
    if (!isOpen || scanStatus !== 'scanning') return;

    const readyForConfig =
      docs.length > 0 || !!detectedPathType || awaitingDocuments;

    if (readyForConfig) {
      setProgress(100);
      setIsScanning(false);
      setScanStatus('completed');
    }
  }, [isOpen, docs.length, scanStatus, detectedPathType, awaitingDocuments]);

  // Safety net: never leave the modal stuck at 90% if polling is slow or returns no preview docs.
  useEffect(() => {
    if (!isOpen || !isScanning) return;

    const timeout = setTimeout(() => {
      setProgress(100);
      setIsScanning(false);
      setScanStatus('completed');
    }, 30000);

    return () => clearTimeout(timeout);
  }, [isOpen, isScanning]);

  useEffect(() => {
    if (detectedPathType && !confirmedPathType) {
      const resolved = detectedPathType === 'gap' ? 'compliance_check' : detectedPathType;
      setConfirmedPathType(resolved);
      if (resolved === 'conflict') setConflictMode('dvd');
    }
  }, [detectedPathType, confirmedPathType]);

  const currentIds = useMemo(() => new Set(docs.map(getId)), [docs]);
  const availableDocs = useMemo(
    () => (projectDocs || []).filter(d => !currentIds.has(getId(d))),
    [projectDocs, currentIds]
  );

  // ── Selection helpers ──────────────────────────────────────────

  const handleDocToggle = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleAddDocsConfirm = (toAdd) => {
    if (!toAdd || toAdd.length === 0) return;
    setDocs(prev => [...prev, ...toAdd]);
    setSelectedDocIds(prev => new Set([...prev, ...toAdd.map(getId)]));
    setShowAddDocsModal(false);
  };

  const handleComplianceTypeToggle = (value) => {
    setSelectedComplianceTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const isDocSectionVisible = useMemo(() => {
    if (!confirmedPathType) return false;
    if (confirmedPathType === 'gap') return true;
    if (confirmedPathType === 'conflict') return !!conflictMode;
    if (confirmedPathType === 'compliance_check') return !!selectedComplianceType;
    return false;
  }, [confirmedPathType, conflictMode, selectedComplianceType]);

  // Pre-select prompt-matched candidates when the document section opens.
  useEffect(() => {
    if (!isOpen || isScanning || !isDocSectionVisible) return;
    if (confirmedPathType === 'conflict' && conflictMode === 'rvr') return;

    const fingerprint = candidatesFingerprintRef.current;
    if (!fingerprint || autoSelectedCandidatesRef.current === fingerprint) return;

    const candidateIds = (candidates || []).map(getId).filter(Boolean);
    if (candidateIds.length === 0) return;

    autoSelectedCandidatesRef.current = fingerprint;
    setSelectedDocIds(new Set(candidateIds));
  }, [
    isOpen,
    isScanning,
    isDocSectionVisible,
    confirmedPathType,
    conflictMode,
    selectedComplianceType,
    candidates,
  ]);

  // ── Continue validation ────────────────────────────────────────

  const canContinue = useMemo(() => {
    if (isScanning || isSubmitting) return false;
    if (!confirmedPathType) return false;

    if (confirmedPathType === 'conflict') {
      if (!conflictMode) return false;
      if (conflictMode === 'dvd') return selectedDocIds.size >= 2;
      return selectedComplianceTypes.length >= 2;
    }

    if (confirmedPathType === 'gap') {
      return selectedDocIds.size >= 2;
    }

    if (confirmedPathType === 'compliance_check') {
      return selectedComplianceType !== '' && selectedDocIds.size >= 1;
    }

    return false;
  }, [
    isScanning, isSubmitting, confirmedPathType, conflictMode,
    selectedDocIds, selectedComplianceType, selectedComplianceTypes
  ]);

  // ── Continue label ─────────────────────────────────────────────

  const continueLabel = useMemo(() => {
    if (isScanning) return 'Scanning...';
    if (isSubmitting) return 'Submitting...';

    if (confirmedPathType === 'conflict') {
      if (!conflictMode) return 'Select a Conflict Type';
      if (conflictMode === 'dvd') {
        return selectedDocIds.size < 2
          ? 'Select at least 2 documents'
          : `Run Conflict Detection (${selectedDocIds.size} docs)`;
      }
      return selectedComplianceTypes.length < 2
        ? 'Select at least 2 compliance types'
        : `Run Conflict Detection (${selectedComplianceTypes.length} types)`;
    }

    if (confirmedPathType === 'gap') {
      return selectedDocIds.size < 2
        ? 'Select at least 2 documents'
        : `Run Gap Detection (${selectedDocIds.size} docs)`;
    }

    if (confirmedPathType === 'compliance_check') {
      if (!selectedComplianceType) return 'Select a compliance type';
      if (selectedDocIds.size === 0) return 'Select at least 1 document';
      return `Run Compliance Check (${selectedDocIds.size} docs)`;
    }

    return 'Continue';
  }, [
    isScanning, isSubmitting, confirmedPathType, conflictMode,
    selectedDocIds, selectedComplianceType, selectedComplianceTypes
  ]);

  // ── Handle continue ────────────────────────────────────────────

  const handleContinue = () => {
    if (!onContinue || !canContinue) return;
    setIsSubmitting(true);

    const selectedDocsArray = docs.filter(d => selectedDocIds.has(getId(d)));

    // Build inputs based on path
    let inputs = {};
    if (confirmedPathType === 'conflict') {
      if (conflictMode === 'dvd') {
        inputs = { deliverable_doc_ids: Array.from(selectedDocIds) };
      } else {
        inputs = { compliance_types: selectedComplianceTypes };
      }
    } else if (confirmedPathType === 'gap') {
      inputs = { deliverable_doc_ids: Array.from(selectedDocIds) };
    } else if (confirmedPathType === 'compliance_check') {
      inputs = {
          compliance_type: selectedComplianceType,
          subcategories: selectedSubcategories,
          deliverable_doc_ids: Array.from(selectedDocIds),
      };
    }

    onContinue(
      selectedDocsArray,
      confirmedPathType,
      conflictMode,
      inputs,
      confirmedPathType === 'conflict' && conflictMode === 'rvr'
        ? selectedComplianceTypes
        : confirmedPathType === 'compliance_check'
          ? [selectedComplianceType]
          : [],
      selectedSubcategories,
    );
  };

  const composePhase = !isScanActive;
  const canStartScan = composePhase && !!String(promptValue || '').trim() && !isRunning;

  if (!isOpen) return null;

  return (
    <div
      className={`compliance-scan-overlay modal-overlay ${isOpen ? 'active' : ''}`}
    >
      <div className="compliance-scan-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="compliance-scan-header">
          <h2 className="compliance-scan-title">Compliance Scan</h2>
          <button
            type="button"
            className="compliance-scan-close"
            onClick={e => { e.stopPropagation(); composePhase ? onClose() : onCancelScan(); }}
            aria-label="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="compliance-scan-body">

          <CompliancePrompt
            className="compliance-prompt--modal"
            showHeader={false}
            showRunButton={false}
            placeholder={t('compliance.promptPlaceholder', 'Type or speak your compliance prompt...')}
            promptValue={promptValue}
            onPromptChange={onPromptChange}
            onRun={onStartScan}
            onVoiceToggle={onVoiceToggle}
            isRecording={isRecording}
            isRunning={isRunning}
            inputDisabled={isScanActive && isRunning}
            showLibrary={showPromptLibrary}
            onToggleLibrary={onTogglePromptLibrary}
            savedPrompts={savedPrompts}
            onSaveCurrent={onSavePrompt}
            onUsePrompt={onUsePrompt}
            onDeletePrompt={onDeletePrompt}
            libraryEmptyText={promptLibraryEmptyText}
          />

          {/* Progress — after user starts a scan */}
          {isScanActive ? (
          <div className="compliance-scan-progress">
            <div className="compliance-scan-progress-header">
              <span className={`compliance-scan-progress-status-icon ${
                scanStatus === 'completed'
                  ? 'compliance-scan-progress-status-icon--completed'
                  : scanStatus === 'error'
                    ? 'compliance-scan-progress-status-icon--error'
                    : 'compliance-scan-progress-status-icon--default'
              }`}>
                {scanStatus === 'completed'
                  ? <FiCheck size={16} />
                  : scanStatus === 'error'
                    ? <FiAlertCircle size={16} />
                    : <FiClock size={16} />}
              </span>
              <span className="compliance-scan-progress-title">
                {isScanning ? 'Scanning Documents...' : 'Scan Complete'}
              </span>
            </div>
            <div className="compliance-scan-progress-bar">
              <div className="compliance-scan-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="compliance-scan-progress-text">
              {isScanning
                ? `${Math.round(progress)}% complete`
                : docs.length
                  ? 'Found relevant documents'
                  : 'Finalizing results…'}
            </div>
          </div>
          ) : null}

          {/* Path Type Detection — only show after scan completes */}
          {isScanActive && !isScanning && confirmedPathType && revealStage >= 1 && (
            <div className="compliance-scan-path-section">
              <div className="compliance-scan-section-label">
                Detected Scan Type
              </div>

              {/* Path reasoning from LLM */}
              {pathReasoning && (
                <div className="compliance-scan-path-reasoning">
                  <FiInfo size={14} />
                  <span>{pathReasoning}</span>
                  {pathConfidence && (
                    <span className="compliance-scan-path-confidence">
                      {Math.round(parseFloat(pathConfidence) * 100)}% confidence
                    </span>
                  )}
                </div>
              )}

              {/* Path type selector — user can override */}
              <div className="compliance-scan-path-selector">
                {Object.entries(PATH_TYPE_LABELS).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`compliance-scan-path-pill ${confirmedPathType === value ? 'compliance-scan-path-pill--active' : ''}`}
                    onClick={() => {
                      const pathChanged = confirmedPathType !== value;
                      setConfirmedPathType(value);
                      if (pathChanged) {
                        // Reset dependent selections when path changes.
                        setSelectedDocIds(new Set());
                        setSelectedComplianceTypes([]);
                        setSelectedComplianceType('');
                        setSelectedSubcategories([]);
                        setConflictMode(value === 'conflict' ? 'dvd' : null);
                        autoSelectedCandidatesRef.current = '';
                      }
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Path description */}
              <div className="compliance-scan-path-description">
                {PATH_TYPE_DESCRIPTIONS[confirmedPathType]}
              </div>
            </div>
          )}

          {/* Compliance Type Selection — Path 3 or conflict rvr */}
          {isScanActive && !isScanning && confirmedPathType && revealStage >= 2 && (
            <>
              {/* Path 3 — single compliance type + optional subcategory */}
              {confirmedPathType === 'compliance_check' && (
                <div className="compliance-scan-section compliance-scan-section--compact">
                  <div className="compliance-scan-section-label">
                    Compliance Type <span className="compliance-scan-required">*</span>
                  </div>
                  <div className="compliance-scan-path-selector">
                    {COMPLIANCE_TYPES.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        className={`compliance-scan-path-pill ${
                          selectedComplianceType === value
                            ? 'compliance-scan-path-pill--active-outline'
                            : ''
                        }`}
                        onClick={() => {
                          setSelectedComplianceType(value);
                          setSelectedSubcategories([]);
                          autoSelectedCandidatesRef.current = '';
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {selectedComplianceType === 'BUILDING_CODE' && (
                    <div className="compliance-scan-subcategory-block">
                      <div className="compliance-scan-section-label">
                        Subcategory{' '}
                        <span className="compliance-scan-optional-badge">optional</span>
                      </div>
                      <div className="compliance-scan-path-selector">
                        {BUILDING_CODE_SUBCATEGORIES.map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            className={`compliance-scan-path-pill ${
                              selectedSubcategories.includes(value)
                                ? 'compliance-scan-path-pill--active'
                                : ''
                            }`}
                            onClick={() =>
                              setSelectedSubcategories(prev =>
                                prev.includes(value)
                                  ? prev.filter(v => v !== value)
                                  : [...prev, value]
                              )
                            }
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Conflict rvr — pick 2+ compliance types */}
              {confirmedPathType === 'conflict' && conflictMode === 'rvr' && (
                <div className="compliance-scan-section compliance-scan-section--compact">
                  <div className="compliance-scan-section-label">
                    Select Compliance Types to Compare <span className="compliance-scan-required">* (min 2)</span>
                  </div>
                  <div className="compliance-scan-path-selector">
                    {COMPLIANCE_TYPES.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        className={`compliance-scan-path-pill ${
                          selectedComplianceTypes.includes(value)
                            ? 'compliance-scan-path-pill--active'
                            : ''
                        }`}
                        onClick={() => handleComplianceTypeToggle(value)}
                      >
                        {label}
                        {selectedComplianceTypes.includes(value) && (
                          <FiCheck size={12} style={{ marginLeft: 4 }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </>
          )}

          {/* Document Selection */}
          {isScanActive && !isScanning && confirmedPathType && revealStage >= 3 && isDocSectionVisible && !(confirmedPathType === 'conflict' && conflictMode === 'rvr') && (
            <div className="compliance-scan-docs">
              <h3 className="compliance-scan-docs-title">
                <FiFileText size={16} />
                {confirmedPathType === 'conflict' || confirmedPathType === 'gap'
                  ? ` Select Documents to Compare (${docs.length} found) — min 2`
                  : ` Select Deliverable Documents (${docs.length} found)`}
              </h3>

              <div className="compliance-scan-doc-pills">
                {docs.map((doc) => {
                  const id = getId(doc);
                  const isSel = selectedDocIds.has(id);
                  return (
                    <div
                      key={id}
                      className={`compliance-scan-doc-pill ${isSel ? 'compliance-scan-doc-pill--selected' : ''}`}
                      onClick={() => handleDocToggle(id)}
                      title={doc.title || doc.name || 'Untitled Document'}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') handleDocToggle(id);
                      }}
                    >
                      <span className="compliance-scan-doc-pill__icon">
                        <FiFileText size={12} />
                      </span>
                      <span>{doc.title || doc.name || 'Untitled Document'}</span>
                      {doc.score != null && (
                        <span className="compliance-scan-doc-pill__score">
                          ({Math.round(doc.score)} pts)
                        </span>
                      )}
                      {isSel && <FiCheck size={12} style={{ marginLeft: 4 }} />}
                    </div>
                  );
                })}
              </div>

              <div className="compliance-scan-add-docs-wrap">
                <button
                  type="button"
                  className="compliance-scan-add-docs-btn"
                  onClick={e => { e.stopPropagation(); setShowAddDocsModal(true); }}
                  disabled={!projectDocs || projectDocs.length === 0}
                  aria-haspopup="dialog"
                  aria-expanded={showAddDocsModal}
                >
                  <FiPlus size={16} />
                  Add Additional Documents
                </button>
              </div>

              <AddAdditionalDocumentsModal
                isOpen={showAddDocsModal}
                onClose={() => setShowAddDocsModal(false)}
                availableDocs={availableDocs}
                onConfirm={handleAddDocsConfirm}
                getId={getId}
              />
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="compliance-scan-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => (composePhase ? onClose() : onCancelScan ? onCancelScan() : onClose())}
          >
            {composePhase ? t('compliance.scan.close', 'Close') : t('compliance.scan.cancel', 'Cancel')}
          </button>
          {composePhase ? (
            <button
              type="button"
              className="filter-apply"
              onClick={() => onStartScan && onStartScan()}
              disabled={!canStartScan}
            >
              {isRunning
                ? t('compliance.actions.scanning', 'Scanning...')
                : t('compliance.scan.startScan', 'Start Scan')}
            </button>
          ) : (
            <button
              type="button"
              className="filter-apply"
              onClick={handleContinue}
              disabled={!canContinue}
            >
              {continueLabel}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ComplianceScanModal;